// ==========================================================================
// SCROLL-REVEAL / MOTION ENGINE
// --------------------------------------------------------------------------
// A small, class-driven animation layer that can be applied to any block:
//
//   .split-text-element-{sf,f,m,s}  HEADLINES ONLY — words split (SplitType) and
//                                   staggered up into view (sf = fastest stagger,
//                                   s = slowest). Wrap in .word-wrapper is created
//                                   automatically. Reveal time scales with word
//                                   count, so don't use on paragraph/body copy —
//                                   use .fade-in instead.
//   .mask-in / .mask-in-up          slides in from left / up behind an
//                                   .overflow-x-hidden (or overflow) mask.
//   .fade-up / .fade-right          fades + translates into view. For cards and
//                                   other discrete UI elements.
//   .fade-in                        fades in only (no translate). For body/
//                                   paragraph/intro copy — reveals as one block
//                                   instead of per-word, so timing doesn't grow
//                                   with text length the way split-text does.
//   .animate-on-load                gets .is-animated immediately on load.
//
// Optional per-element hooks:
//   data-timeout="400"    delay (ms) before the reveal fires.
//   data-animation="foo"  extra class added alongside .is-animated.
//
// Reveal triggering:
//   • Static content reveals on scroll (top crosses 80% of the viewport), or
//     immediately on load if it sits within HERO_ZONE of the top of the
//     document — so a hero reveals on load regardless of viewport height,
//     rather than depending on a lookahead margin that shrinks on shorter
//     real-world viewports.
//   • Content inside GSAP-driven sections (the pinned stats-scroller and the
//     stories/media sliders — see DRIVEN below) is NOT revealed on scroll,
//     because those sections move their own slides via GSAP; revealing on page
//     scroll would fire every stacked slide at once. Instead those sections call
//     window.revealSection(container) / window.retriggerSplitText(container) as
//     each slide is activated, keeping the reveal in sync with what's on screen.
//
// Dependencies (enqueued in inc/enqueue.php): GSAP, SplitType, Lenis.
// Lenis smooth scroll runs on desktop (> 960px) only.
//
// Public API (used by src/js/custom-javascript.js sliders):
//   window.revealSection(container)     reveal any reveal-classed descendants once.
//   window.retriggerSplitText(container) replay the word reveal for a subtree.
// ==========================================================================

( function () {
	// Visitors who ask for reduced motion get the finished state, never the
	// animation. Without this the split-text words stay parked at y:150%
	// (offscreen inside .word-wrapper) and the headline never appears at all,
	// so the copy is permanently invisible rather than merely un-animated.
	const prefersReducedMotion =
		window.matchMedia &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	// Sections whose reveals are driven by their own GSAP slide-activation
	// (see custom-javascript.js) rather than by the global scroll observer.
	// .stats-scroller only drives its own reveals when initStatsScroller()
	// actually runs (innerWidth >= 768 — see custom-javascript.js); below that
	// it bails entirely and its slides would otherwise stay permanently
	// hidden, since nothing else ever calls window.revealSection on them.
	// Fall back to the generic scroll observer in that case.
	//
	// Same story for .story-stage (single-story template): story-scroll.js's
	// GSAP pin/scrub only runs at >=992px and calls window.revealSection
	// itself once content/bio slide in. Below 992px story-scroll.js falls
	// back to a plain flow layout with no GSAP reveal calls, so the generic
	// observer needs to handle it there instead.
	const DRIVEN = window.innerWidth < 768
		? '.stories-slider, .media-slider'
		: window.innerWidth < 992
		? '.stats-scroller, .stories-slider, .media-slider'
		: '.stats-scroller, .stories-slider, .media-slider, .story-stage';

	// Words rise from this offset; enough to clear the .word-wrapper mask.
	const SPLIT_TRAVEL = '110%';
	const SPLIT_EASE = 'power3.out';

	// `stagger` is per word, so run time would grow linearly with word count.
	// `maxStagger` caps the total stagger window, compressing the per-word delay
	// on long copy so it lands in about the same time as a short heading.
	const splitConfigs = [
		{ selector: '.split-text-element-sf', duration: 0.5, stagger: 0.01, delay: 0, maxStagger: 0.3 },
		{ selector: '.split-text-element-f', duration: 0.5, stagger: 0.03, delay: 0, maxStagger: 0.35 },
		{ selector: '.split-text-element-m', duration: 0.4, stagger: 0.035, delay: 0, maxStagger: 0.4 },
		{ selector: '.split-text-element-s', duration: 0.5, stagger: 0.06, delay: 0, maxStagger: 0.45 },
	];

	const splitRegistry = {};
	// Mobile stacks GSAP-driven sections (like .stats-scroller) into plain
	// vertical flow now handled by this same observer instead of a single
	// synchronized slide-activation moment — a larger lookahead here means
	// each element's reveal finishes before it's actually scrolled into
	// view, rather than lagging visibly behind a faster mobile scroll.
	// Interior pages (anything but the homepage) get a slightly larger
	// lookahead than the homepage's tuned pace, so reveals fire a bit sooner
	// as content approaches the viewport — homepage cadence is untouched.
	const isInterior = ! document.body.classList.contains( 'home' );
	const revealLookahead = () => {
		const base = window.innerWidth < 768 ? 1.2 : 0.8;
		return window.innerHeight * ( isInterior ? base + 0.15 : base );
	};
	let winHeightPadded = revealLookahead();
	let registryBuilt = false;

	// .animate-on-load — fire as soon as the script runs.
	document.querySelectorAll( '.animate-on-load' ).forEach( ( el ) => el.classList.add( 'is-animated' ) );

	// Split every .split-text-element-* into words and stash them. Idempotent —
	// safe to call from the load handler or lazily from the public API (the
	// GSAP sliders may reveal a slide before the load event fires).
	function buildRegistry() {
		if ( registryBuilt || typeof SplitType === 'undefined' || typeof gsap === 'undefined' ) {
			return;
		}
		registryBuilt = true;

		splitConfigs.forEach( ( { selector } ) => {
			splitRegistry[ selector ] = { array: [], elemArray: [] };

			document.querySelectorAll( selector ).forEach( ( el ) => {
				if ( el.classList.contains( 'split-class' ) ) {
					return;
				}

				const newSplitText = new SplitType( el, { types: 'words' } );

				newSplitText.words.forEach( ( word ) => {
					const wrapper = document.createElement( 'div' );
					wrapper.classList.add( 'word-wrapper' );
					word.parentNode.insertBefore( wrapper, word );
					wrapper.appendChild( word );
				} );

				// Only park the words offscreen when we actually intend to
				// animate them back in.
				if ( ! prefersReducedMotion ) {
					gsap.set( newSplitText.words, { y: SPLIT_TRAVEL } );
				}

				splitRegistry[ selector ].array.push( newSplitText );
				el.classList.add( 'split-class' );
				splitRegistry[ selector ].elemArray.push( el );
				el.setAttribute( 'split-index', splitRegistry[ selector ].elemArray.indexOf( el ) );
			} );
		} );
	}

	function revealSplitEl( el, cfg ) {
		if ( el.classList.contains( 'active' ) ) {
			return;
		}
		el.classList.add( 'active' );
		const entry = splitRegistry[ cfg.selector ].array[ el.getAttribute( 'split-index' ) ];
		if ( entry ) {
			if ( prefersReducedMotion ) {
				// Snap straight to the resting position — no travel, no stagger.
				gsap.set( entry.words, { y: 0 } );
				return;
			}
			const count   = entry.words.length;
			const stagger = count > 1 && cfg.maxStagger
				? Math.min( cfg.stagger, cfg.maxStagger / ( count - 1 ) )
				: cfg.stagger;

			setTimeout(
				() => gsap.fromTo(
					entry.words,
					{ y: SPLIT_TRAVEL },
					{ y: 0, duration: cfg.duration, stagger: stagger, ease: SPLIT_EASE }
				),
				cfg.delay
			);
		}
	}

	// Elements with a pending data-timeout reveal, keyed by element, so repeat
	// calls (the scroll observer re-checks every frame) can reschedule against
	// current position instead of stacking timers.
	const pendingMaskReveals = new WeakMap();

	function revealMaskEl( el ) {
		if ( el.classList.contains( 'is-animated' ) ) {
			return;
		}
		const baseTimeout = el.dataset.timeout ? parseInt( el.dataset.timeout, 10 ) : 0;
		const animation = el.dataset.animation || '';

		const fire = () => {
			el.classList.add( 'is-animated' );
			if ( animation ) {
				el.classList.add( animation );
			}
			pendingMaskReveals.delete( el );
		};

		if ( baseTimeout === 0 ) {
			fire();
			return;
		}

		// data-timeout staggers (grids/lists of cards) assume the element enters
		// the lookahead zone well ahead of the viewport, so the delay resolves
		// off-screen. On a fast scroll the lookahead zone and the viewport are
		// crossed together, so the same fixed delay would play out in full view
		// as a visible pop-in. Since textIn() re-invokes this every frame while
		// the element is unrevealed, keep re-scaling the remaining wait against
		// current position instead of committing to a stale one-shot timer:
		// once any part of the element is actually on screen (rect.top below
		// the viewport bottom is the "not yet visible" case) the wait collapses
		// toward zero; while it's still fully below the viewport, it keeps the
		// full intended stagger.
		const rect = el.getBoundingClientRect();
		const distanceToViewport = Math.max( 0, rect.top - window.innerHeight );
		const timeout = Math.round( baseTimeout * Math.min( 1, distanceToViewport / winHeightPadded ) );

		const pending = pendingMaskReveals.get( el );
		if ( pending ) {
			clearTimeout( pending.id );
		}
		const id = setTimeout( fire, timeout );
		pendingMaskReveals.set( el, { id } );
	}

	// Reveal every reveal-classed element inside a container, once. Used by the
	// GSAP sliders as each slide is activated.
	window.revealSection = function ( container ) {
		buildRegistry();
		if ( typeof gsap === 'undefined' || ! container ) {
			return;
		}
		splitConfigs.forEach( ( cfg ) => {
			container.querySelectorAll( cfg.selector ).forEach( ( el ) => revealSplitEl( el, cfg ) );
		} );
		container.querySelectorAll( '.mask-in, .mask-in-up, .fade-up, .fade-right, .fade-in' ).forEach( revealMaskEl );
	};

	// Replay the word reveal within a container (e.g. every time a slider slide
	// is navigated to).
	window.retriggerSplitText = function ( container ) {
		buildRegistry();
		if ( typeof gsap === 'undefined' || ! container ) {
			return;
		}
		splitConfigs.forEach( ( cfg ) => {
			container.querySelectorAll( cfg.selector ).forEach( ( el ) => {
				const entry = splitRegistry[ cfg.selector ].array[ el.getAttribute( 'split-index' ) ];
				if ( ! entry ) {
					return;
				}
				el.classList.remove( 'active' );
				el.classList.add( 'active' );
				if ( prefersReducedMotion ) {
					gsap.set( entry.words, { y: 0 } );
					return;
				}
				const timeout = el.dataset.timeout ? parseInt( el.dataset.timeout, 10 ) : 0;
				setTimeout(
					() => gsap.fromTo( entry.words, { y: '150%' }, { y: 0, duration: cfg.duration, stagger: cfg.stagger } ),
					timeout
				);
			} );
		} );
	};

	// Hero content sits just below the header at the very top of the document,
	// so it should always reveal on load — but winHeightPadded scales with
	// window.innerHeight, and on a shorter real-world viewport (e.g. a 13"
	// laptop, ~750px tall) it can end up smaller than the hero's own height,
	// leaving the hero just outside the lookahead margin on the very first
	// check. It then sits unrevealed until a later scroll event happens to
	// bring it in range — reading as "doesn't reveal until you scroll" on
	// load. HERO_ZONE is a fixed distance from the top of the document (not
	// tied to viewport height) generous enough to cover any hero on any real
	// device, so above-the-fold content reveals immediately regardless of how
	// tall the browser window actually is.
	const HERO_ZONE = 1100;
	function isNearTopOfDocument( el, scrolled ) {
		if ( scrolled > 0 ) {
			return false; // only short-circuits the very first, unscrolled check
		}
		const offsetTop = el.getBoundingClientRect().top + scrolled;
		return offsetTop < HERO_ZONE;
	}

	// Scroll observer for static (non-DRIVEN) content.
	function textIn() {
		if ( typeof gsap === 'undefined' ) {
			return;
		}
		const scrolled = window.scrollY;

		splitConfigs.forEach( ( cfg ) => {
			document.querySelectorAll( cfg.selector ).forEach( ( el ) => {
				if ( el.closest( DRIVEN ) ) {
					return; // driven by its section's GSAP activation
				}
				const offsetTop = el.getBoundingClientRect().top + scrolled;
				if ( isNearTopOfDocument( el, scrolled ) || scrolled + winHeightPadded > offsetTop ) {
					revealSplitEl( el, cfg );
				}
			} );
		} );

		document
			.querySelectorAll(
				'.mask-in:not(.is-animated), .mask-in-up:not(.is-animated), .fade-up:not(.is-animated), .fade-right:not(.is-animated), .fade-in:not(.is-animated)'
			)
			.forEach( ( el ) => {
				if ( el.closest( DRIVEN ) ) {
					return;
				}
				const offsetTop = el.getBoundingClientRect().top + scrolled;
				if ( isNearTopOfDocument( el, scrolled ) || scrolled + winHeightPadded > offsetTop ) {
					revealMaskEl( el );
				}
			} );
	}

	function initInteractions() {
		// --- Lenis smooth scroll (desktop only) ---------------------------
		if ( window.innerWidth > 960 && typeof Lenis !== 'undefined' ) {
			const lenis = new Lenis( {
				duration: 1.2,
				easing: ( t ) => ( t === 1 ? 1 : 1 - Math.pow( 2, -10 * t ) ),
				direction: 'vertical',
				gestureDirection: 'vertical',
				smooth: true,
				smoothTouch: false,
			} );

			const raf = ( time ) => {
				lenis.raf( time );
				requestAnimationFrame( raf );
			};
			requestAnimationFrame( raf );

			// Lenis's own scroll event fires on every eased raf tick (finer-
			// grained than the native 'scroll' event it also dispatches), so
			// hook textIn() into it too — keeps the reveal check running at
			// full frame rate through a fast, large scroll instead of relying
			// solely on however often the browser coalesces native scroll events.
			lenis.on( 'scroll', textIn );
		}

		buildRegistry();
		if ( typeof gsap === 'undefined' || typeof SplitType === 'undefined' ) {
			return;
		}

		window.addEventListener( 'scroll', textIn );
		window.addEventListener( 'resize', () => {
			winHeightPadded = revealLookahead();
			textIn();
		} );
		textIn();

		// Reveal the initially-visible slide of each slider (their slide 0 is
		// shown at load by main's GSAP). The pinned stats-scroller reveals its
		// first slide via its own ScrollTrigger, so it's not primed here.
		const firstStory = document.querySelector( '.stories-slider .story-slide' );
		if ( firstStory ) {
			window.revealSection( firstStory );
		}
		document
			.querySelectorAll( '.media-slider .media-slide.is-active' )
			.forEach( ( el ) => window.revealSection( el ) );
	}

	// DOMContentLoaded, not 'load' — 'load' waits on every resource on the
	// page, including full-bleed hero images. On a slow connection that can
	// take several seconds, during which nothing here has run yet: no scroll
	// listener attached, no initial textIn() check, so an above-the-fold
	// mask/fade element just sits in its pre-reveal state, unrevealed, until
	// 'load' finally fires or the user happens to scroll afterward — reading
	// as "the image doesn't reveal until you scroll." Nothing here (GSAP,
	// SplitType, Lenis, the scroll listener) needs images to be loaded first.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', initInteractions );
	} else {
		initInteractions();
	}
} )();
